/**
 * React Query mutation hook for managing per-meeting force-record state.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { apiPut } from '@/lib/http/client';
import { logger } from '@/lib/logger';

interface ApiError extends Error {
  statusCode?: number;
}

interface ForceRecordResponse {
  force_record: boolean;
}

export interface UseForceRecordResult {
  setForceRecord: (value: boolean) => Promise<void>;
  updating: boolean;
  error: string | null;
  reset: () => void;
}

const NON_ATTENDEE_MESSAGE = 'Only meeting attendees can change this setting';

export function useForceRecord(
  orgId: number | undefined,
  meetingEntityId: string | undefined
): UseForceRecordResult {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<ForceRecordResponse, ApiError, boolean>({
    mutationFn: (value: boolean) => {
      if (!orgId || !meetingEntityId) {
        return Promise.reject(
          new Error('orgId and meetingEntityId are required')
        );
      }
      return apiPut<ForceRecordResponse>(
        `/api/orgs/${orgId}/recall/meetings/${meetingEntityId}/force-record/`,
        { force_record: value }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm', orgId, 'object', meetingEntityId],
      });
    },
  });

  const setForceRecord = useCallback(
    async (value: boolean): Promise<void> => {
      setError(null);
      try {
        await mutation.mutateAsync(value);
      } catch (e) {
        const err = e as ApiError;
        if (err.statusCode === 403) {
          setError(NON_ATTENDEE_MESSAGE);
        } else {
          logger.error(
            'Failed to update force-record setting',
            err instanceof Error ? err : new Error(String(err)),
            { component: 'useForceRecord', meetingEntityId }
          );
          setError("We couldn't update the recording. Try again.");
        }
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutateAsync is stable per React Query; the wrapper object isn't
    [mutation.mutateAsync]
  );

  const reset = useCallback(() => setError(null), []);

  return {
    setForceRecord,
    updating: mutation.isPending,
    error,
    reset,
  };
}
